import { useQuery } from "@apollo/client/react";
import { styled } from "@mui/material";
import { FC, Fragment, useState } from "react";
import { useLocation } from "react-router-dom";
import { Export, Import, SourceFilters } from "../../components/Reports";
import { QueryResult } from "../../components/common/QueryResult";
import { SourceBundle, SourceTable as SourceTableType, SourceBundlesDocument } from "../../graphql/types";
import { getQueryParams } from "../../lib/Util";
import { ISourceTable } from "./types";

const ImportTable = styled("table")`
  margin: 20px;
`;

const ImportCell = styled("td")(({ theme }) => ({
  border: `1px solid ${theme.palette.surfaceLight50.main}`,
  padding: "20px",
}));

const SchemaTable = styled("table")`
  margin: 20px;
`;

const SchemaCell = styled("td")(({ theme }) => ({
  border: `1px solid ${theme.palette.text50.main}`,
  padding: "3px",
}));

type SchemaProps = {
  schema: {
    fields: {
      name: string;
      type: {
        sbstype: string;
      };
    }[];
  };
};

const Schema: FC<SchemaProps> = ({ schema }) => {
  return (
    <SchemaTable>
      <tbody>
        {schema.fields.map((field) => {
          return (
            <tr key={field.name}>
              <SchemaCell>{field.name}</SchemaCell>
              <SchemaCell>{field.type.sbstype}</SchemaCell>
            </tr>
          );
        })}
      </tbody>
    </SchemaTable>
  );
};

const SchemaReportTableRows = ({ table, bundle }: { table: SourceTableType; bundle: SourceBundle }) => {
  return (
    <tr key={table.id}>
      <ImportCell>
        <Import bundle={bundle} table={table} />
        <Schema schema={table.schema} />
      </ImportCell>
      <ImportCell>
        {table.exports.map((e) =>
          e ? (
            <Fragment key={e.id}>
              <Export key={e.id} table={e} />
              <Schema schema={e.schema} />
            </Fragment>
          ) : null
        )}
      </ImportCell>
    </tr>
  );
};

const SourceTable: FC<ISourceTable> = ({ filter }) => {
  const variables = { importName: filter.importName, project: filter.project };
  const { loading, error, data } = useQuery(SourceBundlesDocument, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "cache-and-network",
    variables,
  });
  return (
    <QueryResult
      data={data}
      error={error}
      loading={loading}
      nodeName="source_bundles"
      emptyElement="No Sources"
      resultsMapper={(results: SourceBundle[]) => {
        return (
          <ImportTable>
            <tbody>
              {results.map((bundle) => {
                return bundle.tables.map((table, index) => {
                  if (!table) return null;
                  return <SchemaReportTableRows key={index} bundle={bundle} table={table} />;
                });
              })}
            </tbody>
          </ImportTable>
        );
      }}
    />
  );
};

const SchemaReport = () => {
  const location = useLocation();
  const { file, name } = getQueryParams(location.search);

  const [filter, setFilter] = useState<{ importName: string | null; project: string }>({
    importName: name || "",
    project: file || "",
  });

  return (
    <div>
      <SourceFilters {...filter} onChange={setFilter} />
      {filter.project || filter.importName ? <SourceTable filter={filter} /> : <div>Choose a project or import.</div>}
    </div>
  );
};

export { SchemaReport };
